import {
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AgentEvent,
  AgentFile,
  AgentModel,
  AgentNote,
  AgentService,
  AskQuestion,
  ChangeField,
  ChatStream,
  McpConnection,
  ResultDoc,
  ResultFile,
  ResultImage,
  ResultTable,
} from '../agent.service';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {renderMarkdown} from '../markdown';

/**
 * Floating assistant panel, mounted in the shell so it is reachable from every
 * admin screen rather than living behind its own route.
 *
 * Built out of the Beautiful UI primitives (beautifului.dev), which is a
 * decision about grammar rather than decoration: each primitive states one kind
 * of thing, and the panel's job is to keep those kinds apart. What the model is
 * working through, what it touched, what it is proposing to change and what it
 * finally said must never look alike, because the operator's decision differs
 * for each.
 *
 * The pieces used here, and what each one is honest about:
 *  - Thinking      — the trace, in three tabs: steps, reasoning, sources.
 *  - Tool Chips    — what ran, with the arguments it ran with.
 *  - Task Rows     — approved writes, tracked to their outcome.
 *  - Records Table — a tool's own rows.
 *  - Context Cards — guide entries a lookup actually returned.
 *  - Streaming Text— the answer, with its sources, actions and follow-ups.
 *  - Approval Card — the one place the panel asks instead of reports.
 *  - Prompt Bar    — the composer, with commands, scopes and the model picker.
 *
 * Four of the published primitives are deliberately absent. Recommendation Card
 * and Insight Cards need a confidence score and a series the service does not
 * produce, and inventing either would dress a guess as a measurement; Sidebar
 * Nav and Search belong to an application shell, not to a panel.
 */

interface ChatTurn {
  role: 'user' | 'assistant';
  text: string;

  /**
   * Everything up to the last completed paragraph, as HTML.
   *
   * Split from the tail on purpose. The tail streams as individual word spans so
   * each word can animate in exactly once — an `*ngFor` appends rather than
   * rebuilds — while the settled part is real Markdown. Rendering the whole
   * answer either way loses something: all-Markdown cannot animate per word
   * because `[innerHTML]` replaces the subtree on every chunk, and all-words
   * never becomes a list or a table.
   */
  settledHtml: string;
  /** Words after the last paragraph break, still arriving. */
  tail: string[];

  /**
   * Model reasoning, collapsed by default. Held as plain text and rendered as
   * plain text — reasoning is not addressed to the operator and is frequently
   * half-formed, so passing it through the Markdown renderer would dress
   * working-out up as a statement.
   */
  reasoning: string;
  /**
   * Total time spent reasoning across the whole turn, for the "N saniye
   * düşündü" label. The sum of every thinking burst, not the last one.
   *
   * A turn thinks in bursts: it reasons, calls a tool, reasons again on the
   * result, calls another, and reasons once more before answering. Each burst
   * is bounded by closeReasoningClock() at the next tool_start or at the first
   * word of the answer, which is what keeps tool time out of the figure.
   */
  reasoningMs: number;
  /** Bursts already closed. The open one is added on top as it runs. */
  reasoningBase: number;
  /** When the burst in flight began, or 0 when none is. */
  reasoningStart: number;

  /**
   * Tool calls belong to the message that made them, not to the panel. Held on
   * the turn so a second question does not appear to have made the first
   * question's calls.
   */
  tools: ToolActivity[];
  /** Approved writes, tracked separately: they changed something. */
  tasks: ToolActivity[];
  /** Guide entries returned by a lookup, for the context cards. */
  docs: ResultDoc[];
  /**
   * Files the assistant built during this turn.
   *
   * On the turn rather than in the trace: a workbook the operator is meant to
   * open and check before it is imported is part of the answer, not a detail
   * of how the answer was produced.
   */
  files: ResultFile[];
  /**
   * Pictures rendered during this turn — poster previews, and the crop a slot
   * would actually receive.
   *
   * On the turn for the same reason the files are, and with more force: the
   * assistant cannot see these. It reports the measurements and shows the
   * picture, and the judgement of whether the artwork is right belongs to the
   * operator. Buried inside a collapsed trace, that judgement never gets made.
   */
  images: ShownImage[];
  /** Next questions the model offered. Never derived here. */
  followups: string[];
  /**
   * Figures in the reply that appear in no tool result. Empty is the normal
   * case; a non-empty list marks the reply rather than interrupting it.
   */
  ungrounded: string[];
  /**
   * Faults reported during this turn — a provider error, a dropped stream, a
   * model that wrote its tool call as prose.
   *
   * Held rather than written into the answer. These are notes ABOUT the turn,
   * not part of what the assistant said, and pasting a four-line diagnostic
   * into the reply made every one of them louder than the answer it was
   * annotating. They surface as a mark on the action row instead — except when
   * there is no answer at all, in which case settle() puts them back in the
   * open, because a lone icon under an empty bubble is not a report.
   */
  notices: string[];
  /**
   * Writes that ran in Auto mode, in the words the approval card would have
   * used.
   *
   * Auto mode removes the question, not the operator's right to know what was
   * done for them. These are records rather than prompts — the card primitive
   * would be wrong here, because it offers a choice that has already passed.
   */
  autoWrites: {tool: string; preview: string}[];
  /** Notes written during this turn, shown so nothing is learned invisibly. */
  notes: AgentNote[];

  /** The ungrounded-figures explanation is expanded. */
  ungroundedOpen: boolean;

  traceOpen: boolean;
  /**
   * The operator has opened or closed the trace themselves on this turn.
   *
   * Once they have, the panel stops managing it. Without this the auto-open ran
   * on EVERY reasoning delta — a few per second — so closing the trace mid-turn
   * lasted until the next chunk arrived and it sprang back open. The same shape
   * as the transcript's scroll pinning: a rule written as "make it so" rather
   * than "make it so once".
   */
  traceTouched: boolean;
  traceTab: string;
  sourcesOpen: boolean;
  /** Transient "Kopyalandı" state on the action row. */
  copied: boolean;
  /** The turn is still being written into. */
  live: boolean;
}

interface ToolActivity {
  callId: string;
  tool: string;
  /** Tool name without the family prefix — what the row shows. */
  label: string;
  family: string;
  kind: string;
  /** Truncated for the chip. */
  args: string;
  /** Full arguments, revealed when the row is expanded. */
  argsFull: string;
  state: 'running' | 'ok' | 'failed';
  summary: string;
  open: boolean;
  /** A tool's own rows, re-shaped by the service. Never reconstructed here. */
  table: ResultTable | null;
}

/**
 * A picture the assistant rendered, plus the state of fetching it.
 *
 * The service's ResultImage says what it is; `url` is what the browser can
 * actually draw, and it is null until the authorised fetch comes back. `failed`
 * is kept apart from "still loading" so a preview that could not be fetched says
 * so — a poster slot that silently stays blank reads as artwork that is missing
 * from the CMS rather than a request this panel got wrong.
 */
interface ShownImage {
  meta: ResultImage;
  /** The object URL itself, for opening the picture in a tab of its own. */
  url: string | null;
  /**
   * The same URL, marked safe for `[src]`.
   *
   * Angular 8's URL sanitiser allows `https:`, `mailto:`, `ftp:`, `tel:`, `file:`
   * and relative paths — and NOT `blob:`, which it rewrites to `unsafe:blob:…`.
   * The picture then renders as a broken image while the click handler, which
   * gets the raw string and is not sanitised, opens it perfectly: the exact
   * shape of the bug this replaced.
   *
   * Bypassing is right here rather than a hole in the check. The blob was made
   * by this component out of the response to its own authorised fetch of this
   * service's own file route. Nothing about it came from the model.
   */
  src: SafeUrl | null;
  failed: boolean;
}

/**
 * One outstanding card. Both kinds share a shape because both are the same
 * event from the operator's side — the turn has stopped and is waiting on them.
 * What differs is what it costs to answer wrongly, which is why an approval
 * carries the change table and a question does not.
 */
interface PendingCard {
  kind: 'approve' | 'ask';
  callId: string;
  title: string;
  tool: string;
  /**
   * The write described in words — "Arif Bey (id 137) → «Altın Sinema Paketi»
   * (id 6)". Ids alone are not reviewable, and the whole point of the prompt is
   * that the operator can review it.
   */
  preview: string;
  /** The write's arguments as named fields. Proposed values only. */
  changes: ChangeField[];
  args: string;
  argsOpen: boolean;

  questions: AskQuestion[];
  index: number;
  /** Question id → chosen option indexes. */
  picked: {[id: string]: number[]};
  /** Question id → free-text answer. */
  custom: {[id: string]: string};
  sent: boolean;
}

/** One attachment chip, from picked to parsed. */
interface PendingFile {
  /** Local id until the upload answers; then the server's. */
  key: string;
  name: string;
  sizeBytes: number;
  state: 'uploading' | 'ready' | 'failed';
  /** Server id, empty until the upload lands. Only these are sent. */
  id: string;
  kind: string;
  /** "4.812 satır · 7 sütun", or "355×500 px" — what the server found. */
  detail: string;
  problem: string;
}

interface MenuRow {
  key: string;
  name: string;
  hint: string;
  /** Text inserted into the draft. Empty when the row runs a command. */
  insert: string;
  /**
   * The row's glyph, as raw `d` attributes drawn into a shared 24×24 stroke
   * frame.
   *
   * Path data on the row rather than a name the template switches over: a
   * switch means every new row needs a matching *ngIf in the markup, and the
   * one that gets forgotten renders an empty 22px hole. Here a row without
   * paths simply has no icon, which is a state the layout already handles.
   */
  icon?: string[];
  /** A panel action rather than an insert or a command. */
  action?: 'files' | 'connect' | 'notes' | 'manual' | 'auto';
  /**
   * Insert the text verbatim, leave the menu open and re-read the caret.
   *
   * For the two rows that type a sigil. The ordinary insert appends a trailing
   * space, which for "@" would end the token the moment it was created and shut
   * the menu the row exists to open.
   */
  raw?: boolean;
}

/* Glyphs, named so the tables below read as content rather than as path data. */
const I_CLIP = ['M21 11.5l-8.6 8.6a5 5 0 0 1-7.1-7.1l8.7-8.7a3.4 3.4 0 0 1 4.8 4.8l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9'];
const I_NEW = ['M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6', 'M18.4 2.6a2 2 0 0 1 2.8 2.8L12.5 14 9 15l1-3.5z'];
const I_STOP = ['M9 9h6v6H9z', 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z'];
const I_MEMORY = ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6'];
const I_SHARE = ['M18 5.5m-2.6 0a2.6 2.6 0 1 0 5.2 0 2.6 2.6 0 1 0-5.2 0', 'M6 12m-2.6 0a2.6 2.6 0 1 0 5.2 0 2.6 2.6 0 1 0-5.2 0', 'M18 18.5m-2.6 0a2.6 2.6 0 1 0 5.2 0 2.6 2.6 0 1 0-5.2 0', 'M8.3 10.8 15.7 6.7M8.3 13.2l7.4 4.1'];
const I_SHIELD = ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'];
const I_BOLT = ['M13 2L4.5 13H11l-1 9 8.5-11H12l1-9z'];
const I_CHART = ['M4 20V10M10 20V4M16 20v-7M22 20H2'];
const I_FILM = ['M3 4h18v16H3z', 'M7 4v16M17 4v16M3 12h18'];
const I_BOOK = ['M4 4.5A2.5 2.5 0 0 1 6.5 2H20v16H6.5A2.5 2.5 0 0 0 4 20.5z', 'M4 20.5A2.5 2.5 0 0 1 6.5 18H20v4H6.5A2.5 2.5 0 0 1 4 20.5z'];
const I_USER = ['M12 12m-4 0a4 4 0 1 0 8 0 4 4 0 1 0-8 0', 'M4.5 21a7.5 7.5 0 0 1 15 0'];
const I_LAYERS = ['M12 2 2 7l10 5 10-5-10-5z', 'M2 17l10 5 10-5M2 12l10 5 10-5'];
const I_TV = ['M3 7h18v12H3z', 'M8 3l4 4 4-4'];
const I_GEAR = ['M12 12m-3 0a3 3 0 1 0 6 0 3 3 0 1 0-6 0', 'M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.3a2 2 0 1 1-4 0v-.2a1.6 1.6 0 0 0-2.8-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3.5 15H3a2 2 0 1 1 0-4h.2A1.6 1.6 0 0 0 4.3 8.2l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.6 1.6 0 0 0 9.8 4.3V4a2 2 0 1 1 4 0v.2a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.3a2 2 0 1 1 0 4h-.2a1.6 1.6 0 0 0-1.1 1z'];
const I_CLOCK = ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M12 7v5l3.2 2'];

/**
 * The one row of the "+" menu.
 *
 * Attaching lives here rather than behind a paperclip of its own: the reference
 * bar has exactly five controls — plus, input, model, dictation, send — and a
 * sixth button would be the one part of this panel that is not the primitive it
 * claims to be built from.
 */
/**
 * The two sigils, as rows.
 *
 * "+" is where an operator goes when they do not yet know what the bar can do,
 * so the two things reachable only by typing a punctuation mark nobody
 * mentioned are offered here as well. They do not duplicate the menus — they
 * type the character and hand over to them.
 */
const SIGIL_ROWS: MenuRow[] = [
  {key: 'sigil-at', name: '@ kapsam', hint: 'Abone, içerik, kanal, analitik…', insert: '@', raw: true, icon: I_LAYERS},
  {key: 'sigil-slash', name: '/ komut', hint: 'Oturum, mod, dosya, bağlantı', insert: '/', raw: true, icon: I_BOLT},
];

const FILE_ROW: MenuRow = {
  key: 'dosya',
  name: 'Dosya ekle',
  hint: 'Excel, CSV, PDF, JSON, görsel',
  insert: '',
  icon: I_CLIP,
  action: 'files',
};

/**
 * Commands the panel actually has.
 *
 * Every row here drives a method that already exists, and nothing is
 * aspirational — `/model` is absent because the picker it opened is parked (see
 * the model picker section), and a command that opens nothing is worse than one
 * that is missing.
 *
 * The list is deliberately the same set of things the chrome can do, not a
 * subset of it: an operator who has learned to type "/" should not have to
 * remember which half of the panel's actions were considered worth a command.
 */
const COMMANDS: MenuRow[] = [
  {key: 'yeni', name: '/yeni', hint: 'Oturumu sıfırla', insert: '', icon: I_NEW},
  {key: 'dosya', name: '/dosya', hint: 'Dosya ekle', insert: '', icon: I_CLIP, action: 'files'},
  {key: 'hafiza', name: '/hafıza', hint: 'Hatırladıklarını gör ve sil', insert: '', icon: I_MEMORY},
  {key: 'manual', name: '/manual', hint: 'Yazma işlemlerini onaya bağla', insert: '', icon: I_SHIELD, action: 'manual'},
  {key: 'auto', name: '/auto', hint: 'Yazma işlemleri sorulmadan çalışsın', insert: '', icon: I_BOLT, action: 'auto'},
  {key: 'baglanti', name: '/bağlantı', hint: 'MCP sunucu adresi ve başlığı', insert: '', icon: I_SHARE, action: 'connect'},
  {key: 'durdur', name: '/durdur', hint: 'Süren turu kes', insert: '', icon: I_STOP},
];

/**
 * Scopes, offered on "@".
 *
 * These are not filters the panel applies — it has none. Each inserts a word
 * the service's tool selector matches, which is the whole of what they do, and
 * the hint says so. A row that implied the panel could restrict the assistant
 * to one area would be claiming a control that does not exist.
 */
const SCOPES: MenuRow[] = [
  {key: 'abone', name: '@abone', hint: 'Müşteri, paket, abonelik', insert: 'abone', icon: I_USER},
  {key: 'icerik', name: '@içerik', hint: 'İçerik, lisans, yayın planı', insert: 'içerik', icon: I_LAYERS},
  {key: 'kanal', name: '@kanal', hint: 'Kanal, EPG, catch-up', insert: 'kanal', icon: I_TV},
  {key: 'analitik', name: '@analitik', hint: 'İzlenme ve kullanım verisi', insert: 'analitik', icon: I_CHART},
  {key: 'katalog', name: '@katalog', hint: 'IMDb, afiş, künye arama', insert: 'katalog', icon: I_FILM},
  {key: 'ayar', name: '@ayar', hint: 'Sistem ayarları, diller, cihazlar', insert: 'ayar', icon: I_GEAR},
  {key: 'denetim', name: '@denetim', hint: 'Kim ne zaman ne değiştirdi', insert: 'denetim', icon: I_CLOCK},
  {key: 'kilavuz', name: '@kılavuz', hint: 'Nasıl yapılır rehberi', insert: 'kılavuz', icon: I_BOOK},
];

/**
 * The empty panel's opening moves.
 *
 * Chips rather than a paragraph explaining what the assistant can do: a first
 * question that runs on one press teaches more about the panel than any
 * description of it, and it costs the operator nothing to find out.
 *
 * The pool is long and only four of it are shown, drawn at random each time the
 * panel opens. Four fixed questions taught four things and then became
 * furniture — an operator who had read them once had no reason to look again,
 * and the ninety-odd capabilities they did not mention stayed invisible. A
 * rotating draw makes the empty panel worth reading every time, and the shuffle
 * button under it turns the rest of the list into something browsable.
 *
 * These are JOBS, not queries. The first version of this pool was a hundred
 * one-line lookups — "Aktif abone sayısı kaç?" — which is not what anyone opens
 * this panel to do, and which taught that the assistant is a search box. A
 * suggestion has to look like the thing on the operator's actual list: several
 * steps, an outcome, and often a file. "Bu hafta eklenen filmlerden afişi eksik
 * olanları bul, IMDb'den doldur" is one press and four tool calls, and it is
 * also a real Tuesday.
 *
 * Every line is answerable by tools this deployment actually has, grouped below
 * by the family that serves it. Nothing aspirational: a chip that runs into
 * "bunu yapamıyorum" is worse than no chip. Nothing here names a specific
 * customer, package or title either — the pool ships with the build and the
 * data does not, so a suggestion that assumes a row exists is a suggestion that
 * fails on the deployment where it does not.
 */
const STARTER_POOL: string[] = [
  // Abone, paket, abonelik — crm_*
  'Bu ay süresi dolacak abonelikleri çıkar, hangi paketlerde yoğunlaştığını göster',
  'Geçen hafta süresi dolan abonelerin listesini excel yap',
  'Yeni bir kampanya paketi oluştur ve üç aylık dönem tanımla',
  'Bir paketin kanal gruplarını başka bir pakete kopyala',
  'Deneme süresi bitmiş ama ücretliye geçmemiş müşterileri bul',
  'Bir müşterinin paketlerini ve bitiş tarihlerini özetle',
  'Telefon numarasıyla müşteri bul, aboneliklerini listele',
  'Test müşterisi oluştur ve üzerine spor paketi tanımla',
  'Artık kullanılmayan test müşterilerini bul ve temizle',
  'Pasif paketleri listele, hangileri hâlâ aboneye bağlı bak',
  'Bir paketin dönemlerini toplu olarak otuz gün uzat',
  'Aynı e-posta ile kayıtlı birden fazla müşteri var mı kontrol et',
  'Bir pakete içerik grubu ekle ve değişikliği doğrula',
  'Bu ay iptal edenleri say, geçen ayla karşılaştır',
  'Dondurulmuş abonelikleri kaç gündür dondurulduklarıyla listele',
  'Bir müşterinin dondurulmuş aboneliğini yeniden başlat',
  'Paket başına aktif abone dağılımını çıkar',
  'Bir paketten spor grubunu kaldır, etkilenen abone sayısını söyle',
  'Toplu paket vermeden önce kaç müşterinin etkileneceğini hesapla',
  'Dönemi tanımsız kalmış paketleri bul',
  'Yeni müşteri kaydı oluştur ve başlangıç paketini ver',
  'Bir müşterinin iletişim bilgilerini güncelle',

  // İçerik, lisans, yayın planı — cms_*, catalog_*
  'Bu hafta eklenen filmlerden afişi eksik olanları bul, IMDb’den doldur',
  'Yayına hazır olmayan içerikleri listele, her birinin eksiğini say',
  'Yeni bir dizi oluştur, ilk sezonun bölümlerini IMDb’den çek',
  'Lisansı bu ay bitecek içerikleri çıkar, yayın planlarını kontrol et',
  'Bir içeriğin künyesini IMDb’den güncelle ve oyuncu kadrosunu doldur',
  'Yayın planı olmayan içerikleri bul ve neden eksik olduklarını açıkla',
  'Bir içerik için lisans oluştur ve plan profiline bağla',
  'Afiş tanımlarını listele, hangi boyutların eksik olduğunu göster',
  'Bir dizinin eksik kalan bölümlerini bul',
  'İçerik gruplarını listele, boş olanları işaretle',
  'Bir filmi IMDb’de bul ve katalogda oluştur',
  'Yayından kalkmış ama hâlâ pakete bağlı içerikleri bul',
  'Bu ayın yeni içeriklerini tür bazında özetle',
  'Bir içeriğin afişini önizle, sorunluysa yenisini yükle',
  'Aynı isimde birden fazla içerik kaydı var mı kontrol et',
  'Bir dizinin tüm bölümleri için yayın planı oluştur',
  'Lisansı olmayan içerikleri listele ve öncelik sırasına koy',
  'Katalogda ara, sonuçları IMDb puanına göre sırala',
  'Bir planı aktifleştirmeden önce eksik alan var mı kontrol et',
  'Çocuk kategorisindeki içerikleri listele, yaş etiketlerini doğrula',
  'Tür listesini çıkar, hangi türlerde içerik az olduğunu göster',
  'Fragmanı olmayan içerikleri bul',
  'Bir içeriğin bütün afişlerini tek seferde doldur',
  'Platformları listele, hangi içeriğin nerede yayında olduğunu göster',

  // Kanal, EPG, catch-up — btv_*
  'Kanal listesini çıkar, planlaması kapalı olanları işaretle',
  'Bugünün yayın akışını kontrol et, boşluk kalan kanalları bul',
  'Bir kanalın yarınki programlarını göster',
  'Onay bekleyen catch-up taleplerini listele',
  'Bir programdan catch-up oluştur ve yayına al',
  'Kanal gruplarını listele, hangi paketlerde kullanıldıklarını göster',
  'EPG dosyası yükle ve sonucunu doğrula',
  'Oluşturulmuş ama yayınlanmamış catch-up kayıtlarını bul',
  'Bir kanalın bilgilerini güncelle',
  'Kanal kategorilerini ve türlerini listele',
  'Bir kanalın planlamasını aç',
  'Bu hafta hiç programı olmayan kanalları bul',
  'Bir programın adını ve saatini düzelt',
  'Platform bazında kanal sayısını çıkar',
  'Yeni bir kanal grubu oluştur ve kanalları içine al',
  'Temizlenebilecek eski catch-up kayıtlarını listele',

  // İzlenme ve kullanım — analytics_*
  'Geçen haftanın en çok izlenen on içeriğini çıkar',
  'Dünkü izlenmeyi bir hafta önceki aynı günle karşılaştır',
  'Saat bazında izlenme dağılımını çıkar, prime time’ı belirle',
  'Cihaz tiplerine göre izlenme oranlarını göster',
  'Bu ayın toplam izlenme süresini geçen ayla karşılaştır',
  'Yarıda bırakılan içerikleri bul, ortalama izlenme oranını hesapla',
  'Hiç izlenmemiş içerikleri listele',
  'En aktif yirmi aboneyi izlenme süresine göre çıkar',
  'Canlı yayın ile VOD izlenmesini oranla',
  'Tür bazında izlenme dağılımını çıkar',
  'Hafta içi ve hafta sonu izlenme farkını göster',
  'Bu ay hiç giriş yapmamış aboneleri bul',
  'Kanal bazında günlük izlenme sayılarını çıkar',
  'İzlenme tablolarında hangi alanların olduğunu göster',
  'Yeni eklenen içeriklerin ilk hafta performansını ölç',
  'Şehir bazında izlenme dağılımını çıkar',

  // Sistem, dil, cihaz — s360_*
  'Tanımlı dilleri listele, çevirisi eksik anahtarları bul',
  'Sistem ayarlarını çıkar, varsayılandan farklı olanları işaretle',
  'Cihaz listesini çıkar, aktif tipleri say',
  'İstemci sürümlerini listele, geride kalanları göster',
  'Bir çeviri metnini bul ve güncelle',
  'Sonuç kodlarını listele, açıklaması eksik olanları bul',
  'Modül ayarlarını göster',
  'Bir sistem ayarını değiştir ve neyi etkileyeceğini açıkla',
  'Yeni bir dil ekle ve temel çevirileri hazırla',
  'Bir kayıt tipindeki bütün kayıtları listele',

  // Denetim, dosya, kılavuz — audit_*, cpi_*, howto_*
  'Bugün yapılan paket değişikliklerini denetim kaydından çıkar',
  'Son yirmi dört saatte silinen içerikleri bul',
  'Dün gece kimin hangi işlemleri yaptığını özetle',
  'Elimdeki excel’deki abonelerin sistemdeki durumunu kontrol et',
  'Bu CSV’deki içerik listesini katalogla karşılaştır, eksikleri çıkar',
  'Yüklediğim listedeki müşterilere toplu paket ver',
  'Bu tabloyu özetle ve dikkat çeken satırları işaretle',
  'Bu ekran görüntüsündeki hatayı açıkla',
  'İki paketi karşılaştır, farklarını tablo yap',
  'Eksik alanı olan içerik kayıtlarını excel olarak çıkar',
  'CPI satırlarını listele, aktifleştirilmeyi bekleyenleri göster',
  'Yeni bir paket oluşturmanın adımlarını anlat',
];

/** How many of the pool the empty panel offers at once. */
const STARTER_COUNT = 4;

/**
 * The standing notice under the header, and the mark that remembers it was
 * dismissed.
 *
 * Versioned rather than a plain boolean: a dismissal is the operator saying "I
 * have read THIS". If the wording changes — a new caveat, a new class of thing
 * the assistant can get wrong — the old dismissal no longer stands for
 * anything, and bumping this shows the notice again to everyone. So bump it
 * whenever the text below changes.
 */
const DISCLAIMER_VERSION = '2026-08-2';
const DISCLAIMER_KEY = 'atlas.agent.disclaimer';

/* ---- Who sees this at all -------------------------------------------------
   While the assistant is in development it is shown only to the accounts meant
   to be exercising it. Everyone else gets no launcher and no panel — not a
   disabled one, and not one that opens and then explains it is unavailable.

   Matched against what login stores: `user.role` (the role's NAME, set in
   login.component.ts) and `username`. Comparison is case-insensitive and exact.

   TO OPEN IT UP, this is the only place to edit: add the role or the username
   here, or empty both lists to show it to everyone. Nothing else in the panel
   knows about this. */
const AGENT_ROLES: string[] = ['llm'];
const AGENT_USERS: string[] = ['llm'];

/** Tool family → the label and swatch used for step rows and source chips. */
const FAMILIES: {[prefix: string]: {label: string; tone: string}} = {
  analytics: {label: 'Analitik', tone: 'blue'},
  catalog: {label: 'Katalog', tone: 'orange'},
  cms: {label: 'İçerik', tone: 'green'},
  crm: {label: 'Abone', tone: 'blue'},
  btv: {label: 'Kanal', tone: 'orange'},
  s360: {label: 'Ayarlar', tone: 'grey'},
  howto: {label: 'Kılavuz', tone: 'red'},
  audit: {label: 'Denetim', tone: 'grey'},
  cpi: {label: 'CPI', tone: 'green'},
  tools: {label: 'Araç seti', tone: 'grey'},
  ask: {label: 'Soru', tone: 'grey'},
  suggest: {label: 'Öneri', tone: 'grey'},
};

@Component({
  selector: 'app-agent-chat',
  templateUrl: './agent-chat.component.html',
  styleUrls: ['./agent-chat.component.css'],
})
export class AgentChatComponent implements OnInit, OnDestroy {
  @ViewChild('scroller', {static: false}) scroller: ElementRef;
  @ViewChild('input', {static: false}) input: ElementRef;
  /** Hidden <input type="file">; the visible control is the clip button. */
  @ViewChild('fileInput', {static: false}) fileInput: ElementRef<HTMLInputElement>;
  /** The control row, measured to decide whether the input needs its own line. */
  @ViewChild('composerRow', {static: false}) composerRow: ElementRef<HTMLElement>;
  /** Off-screen copy of the draft, used to measure its natural width. */
  @ViewChild('measure', {static: false}) measure: ElementRef<HTMLElement>;

  /** The "@" / "/" menu, so arrowing past its edge scrolls rather than hides. */
  @ViewChild('menuEl', {static: false}) menuEl: ElementRef<HTMLElement>;

  open = false;
  busy = false;
  draft = '';

  /**
   * The four opening moves currently on offer.
   *
   * Held in a field rather than drawn by a template method. A method would be
   * re-run on every change-detection pass, so the chips would reshuffle
   * themselves while the operator was reading them — and the one they reached
   * for would be gone by the time the cursor got there.
   */
  starters: string[] = [];

  /**
   * Ghost text in the empty composer, taken with Tab.
   *
   * Before the first turn it is a job from the same pool the empty panel draws
   * its chips from. Afterwards it is the model's own next-step suggestion for
   * the answer just given — see settle() — which is the only source that can be
   * right about a conversation nobody wrote in advance.
   */
  suggestion = '';

  /**
   * The development-stage notice under the header.
   *
   * Open until it is dismissed, and the dismissal is remembered — this is a
   * banner, not a consent gate, and one that came back on every reload would be
   * closed reflexively rather than read.
   */
  disclaimerOpen = true;

  /**
   * Files attached to the message being composed.
   *
   * They are already ON the server by the time they appear here — the upload
   * starts the moment the file is picked, so the parse is done and the chip can
   * say "3 sayfa · 4.812 satır" before the operator has finished typing. A file
   * that failed carries its reason and is still shown: a chip that vanished
   * would leave them wondering whether it went.
   */
  attached: PendingFile[] = [];
  /** True while a drag is over the panel, for the drop hint. */
  dragging = false;
  /**
   * The transcript is at the bottom, so new content should follow.
   *
   * Public because the composer shows a jump-to-latest button whenever it is
   * false: a panel that has quietly stopped following a streaming answer has to
   * say so, and offer the way back in one press.
   */
  stick = true;
  /**
   * The draft no longer fits beside the controls, so it takes a line of its own
   * and they drop below it.
   *
   * The reference composer does exactly this — one row while the message is
   * short, two once it is not — and it is the part that makes the bar feel
   * right at both sizes. Fixed at one row, a real question types itself into a
   * three-word-wide slot; fixed at two, the bar stands a head taller than it
   * needs to for "kaç abone var".
   */
  wide = false;
  private fileCounter = 0;

  provider = '';

  /**
   * Manual asks before every write; Auto does not.
   *
   * Deliberately NOT persisted. A mode that survives a reload is a mode nobody
   * chose this session, and the one it would survive as is the one that stops
   * asking — so the panel opens in Manual every time and the operator turns it
   * off knowingly. The value rides on every request rather than being stored
   * server-side, so what the prompt bar shows and what the service applies are
   * the same thing.
   */
  approvalMode: 'manual' | 'auto' = 'manual';
  model = '';
  /** Model chosen in the picker, sent when the next session is created. */
  modelChoice = '';
  availableProviders: string[] = [];
  providerModels: AgentModel[] = [];
  healthError = '';

  sessionId = '';
  turns: ChatTurn[] = [];
  pending: PendingCard[] = [];

  /** Everything the assistant has carried forward, when the drawer is open. */
  notes: AgentNote[] = [];
  notesOpen = false;
  notesLoading = false;

  /**
   * Ready-made registrations for the operator's own AI client.
   *
   * Built when the drawer opens rather than at construction: the token can be
   * renewed while the panel sits there, and a snippet copied from a stale one
   * fails in somebody else's terminal, where nothing explains why.
   */
  connectOpen = false;
  /**
   * The header's overflow menu.
   *
   * The two shelves below — what it has learned, and how to point another client
   * at the same tools — used to sit in the header as icons of their own. Neither
   * is reached often enough to earn a permanent glyph beside "new session" and
   * "close", and a notebook and a chain link next to each other read as
   * decoration rather than as two different places to go.
   */
  moreOpen = false;

  /** Endpoint and header for the operator's own client, built when opened. */
  connection: McpConnection | null = null;
  /** Which field was just copied ('url' | 'token'), for the confirmation. */
  copiedField = '';
  private copiedTimer: any;
  /**
   * Show the real token instead of the placeholder.
   *
   * Off by default — this is a live credential on a screen that is often shared
   * — but available, because "copy" can fail silently in a browser and the
   * operator then needs something they can select.
   */
  connectRevealed = false;

  /** The "+" / "/" / "@" menu above the composer. */
  menuOpen = false;
  menuKind = '';
  modelMenuOpen = false;
  modelQuery = '';
  /** Index the gliding highlight sits on. */
  active = 0;
  listening = false;

  private stream: ChatStream;
  private recognition: any;
  /** The mic is switched on. Survives the recogniser restarting underneath it. */
  private wantDictation = false;
  /** Draft text plus every dictated phrase the recogniser has finalised. */
  private dictated = '';
  /** How many results of the current session have been committed to `dictated`. */
  private finals = 0;

  constructor(
    private agent: AgentService,
    private zone: NgZone,
    private sanitizer: DomSanitizer,
  ) {}

  /**
   * Whether this operator sees the assistant at all.
   *
   * Read once, on init. A getter re-evaluated by change detection would let the
   * panel appear or vanish mid-session on a storage write from somewhere else,
   * and a floating window that comes and goes on its own is worse than either
   * state.
   */
  visible = false;

  /** Case-insensitive, exact, and blank means "no opinion" rather than "deny". */
  private static permits(list: string[], value: string | null): boolean {
    if (!value) {
      return false;
    }
    const needle = value.trim().toLowerCase();
    for (let i = 0; i < list.length; i++) {
      if (list[i].toLowerCase() === needle) {
        return true;
      }
    }
    return false;
  }

  /**
   * What the composer offers next, BEFORE anything has been asked.
   *
   * A job from the same pool the empty panel draws its chips from. Only ever
   * used on a fresh session: once a turn has happened the suggestion has to
   * come from that turn — see continueFrom().
   */
  private drawSuggestion(): void {
    // A few tries rather than a loop: at a hundred entries a repeat is rare,
    // and a guard that cannot terminate is worse than the repeat it prevents.
    let next = this.suggestion;
    for (let i = 0; i < 5 && next === this.suggestion; i++) {
      next = STARTER_POOL[Math.floor(Math.random() * STARTER_POOL.length)];
    }
    this.suggestion = next;
  }

  /**
   * The next step for the answer that just landed.
   *
   * This used to fall back to a random line from the starter pool whenever the
   * model produced no follow-ups of its own, and that fallback was the whole
   * problem: after "kaç kullanıcı var" → "5 kullanıcınız var", the bar would
   * offer "Bir kanalın planlamasını aç". A suggestion that changes the subject
   * is worse than an empty box, because the operator has to read it to find
   * out it is useless. So when nothing on-topic can be said, nothing is.
   *
   * Order is most-specific-first, and every rule reads a fact about the turn
   * rather than about its wording:
   *
   *   1. The model's own follow-up. On topic by construction — it was written
   *      knowing what had just been answered.
   *   2. A step failed. Nothing else matters until that is dealt with.
   *   3. A write ran. The next thing anyone does is check it landed.
   *   4. Rows came back and no file was produced. "5 kullanıcınız var" is five
   *      rows the operator can have as a spreadsheet, which is the example this
   *      whole mechanism was rebuilt around.
   *   5. Guide entries were read. The question was "how", so offer the steps.
   *
   * Falling off the end returns '' and the ghost simply does not appear.
   */
  private continueFrom(turn: ChatTurn): void {
    this.suggestion = '';

    if (turn.followups.length) {
      this.suggestion = turn.followups[0];
      return;
    }

    const calls = turn.tools.concat(turn.tasks);
    for (let i = 0; i < calls.length; i++) {
      if (calls[i].state === 'failed') {
        this.suggestion = 'Başarısız adımı yeniden dene';
        return;
      }
    }

    if (turn.tasks.length) {
      this.suggestion = 'Yapılan değişiklikleri doğrula';
      return;
    }

    if (!turn.files.length) {
      let rows = false;
      for (let i = 0; i < calls.length; i++) {
        if (calls[i].table && calls[i].table.rowCount > 0) {
          rows = true;
        }
      }
      if (rows || AgentChatComponent.looksTabular(turn.text)) {
        this.suggestion = 'Excel tablosuna dök';
        return;
      }
    }

    if (turn.docs.length) {
      this.suggestion = 'Adımları sırayla anlat';
    }
  }

  /**
   * Whether the answer is a set of things rather than a sentence.
   *
   * A Markdown table, or two or more list items. One bullet is a sentence with
   * a dash in front of it; two is a list, and a list is something that can be a
   * spreadsheet.
   */
  private static looksTabular(text: string): boolean {
    if (/^\s*\|.*\|/m.test(text)) {
      return true;
    }
    const bullets = text.match(/^\s*(?:[-*]|\d+\.)\s+/gm);
    return Boolean(bullets && bullets.length > 1);
  }

  /**
   * Whether the ghost is on screen — and therefore whether Tab takes it.
   *
   * Only over an empty box. A suggestion drawn behind half-typed text would
   * have to complete what is already there, and a stored sentence almost never
   * continues an arbitrary prefix; what it would actually do is put grey words
   * under the caret that Tab then replaces with something else entirely.
   */
  showSuggestion(): boolean {
    return Boolean(
      this.suggestion && !this.draft && !this.busy && !this.listening && this.provider,
    );
  }

  acceptSuggestion(): void {
    if (!this.showSuggestion()) {
      return;
    }
    this.draft = this.suggestion;
    this.onDraftChange();
    this.focusInput();
  }

  ngOnInit(): void {
    // An empty allowlist is the open state: it is what an operator editing the
    // constants at the top would reach for to turn the gate off.
    this.visible =
      (!AGENT_ROLES.length && !AGENT_USERS.length) ||
      AgentChatComponent.permits(AGENT_ROLES, this.readStore('user.role')) ||
      AgentChatComponent.permits(AGENT_USERS, this.readStore('username'));

    if (!this.visible) {
      // No launcher, no panel, and no call to the agent service: an operator
      // who cannot open it should not be probing it either, and the health
      // request is what would otherwise log a failure per page load on every
      // deployment where the service is not running yet.
      return;
    }

    this.rollStarters();
    this.drawSuggestion();
    this.disclaimerOpen = this.readStore(DISCLAIMER_KEY) !== DISCLAIMER_VERSION;
    // Drive the provider picker off the service rather than a hard-coded list:
    // a provider whose key is missing renders as unavailable instead of failing
    // on the operator's first message.
    this.agent.health().then(
      health => {
        this.availableProviders = Object.keys(health.providers).filter(p => health.providers[p]);
        this.provider = health.defaultProvider;
        if (this.availableProviders.indexOf(this.provider) === -1 && this.availableProviders.length) {
          this.provider = this.availableProviders[0];
        }
        if (!this.availableProviders.length) {
          this.healthError = 'Hiçbir model sağlayıcısı yapılandırılmamış.';
        } else {
          this.loadModels();
        }
      },
      () => {
        this.healthError = 'Ajan servisine ulaşılamıyor.';
      },
    );
  }

  ngOnDestroy(): void {
    this.stop();
    this.stopDictation();
    for (const url of this.objectUrls) {
      URL.revokeObjectURL(url);
    }
    this.objectUrls = [];
  }

  toggle(): void {
    this.open = !this.open;
    // The composer is created with the panel, and nothing had sized it: the
    // textarea kept its one-line height while the placeholder wrapped inside
    // it, which showed as clipped text and a scrollbar before a key was
    // pressed. Sizing runs on open rather than on first keystroke.
    if (this.open) {
      setTimeout(() => this.autoGrow(), 0);
      // Reopening an empty panel is a fresh look at it, so it gets a fresh
      // draw. A conversation in progress keeps whatever it had — the chips are
      // not on screen and rerolling them would only surprise the operator later.
      if (!this.turns.length) {
        this.rollStarters();
      }
    }
  }

  /* ---- Composer ---------------------------------------------------------- */

  send(): void {
    // A file on its own is a legitimate message — the operator drops a
    // spreadsheet and expects to be told what is in it. Uploading files are
    // not: sending before the parse lands would reference an id the service
    // does not have yet.
    const ready = this.attached.filter(f => f.state === 'ready');
    const message =
      this.draft.trim() ||
      (ready.length ? 'Ekteki dosyayı incele ve ne olduğunu özetle.' : '');
    if (!message || this.busy || !this.provider || this.uploading) {
      return;
    }
    // A slash command is an instruction to the panel, not a question for the
    // model. Running it and clearing the box is the whole of it.
    if (this.runCommand(message)) {
      return;
    }

    this.turns.push(this.blankTurn('user', message));
    const turn = this.blankTurn('assistant', '');
    turn.live = true;
    this.turns.push(turn);
    this.draft = '';
    this.dictated = '';
    this.stopDictation();
    // The box was sized to the message that just left it.
    setTimeout(() => this.autoGrow(), 0);
    this.closeMenus();
    this.busy = true;
    // Asking a question is a request to see the answer, wherever they had
    // scrolled to while reading the last one.
    this.stick = true;
    this.scrollSoon();

    const fileIds = ready.map(f => f.id);
    this.attached = [];

    this.stream = this.agent.chat(
      message,
      this.provider,
      this.sessionId,
      {
        onEvent: event => this.apply(event),
        onDone: () => {
          this.settle();
          // A dropped connection can never answer an outstanding prompt, so do
          // not leave cards on screen pretending to be live.
          this.pending = [];
          this.scrollSoon();
        },
        onError: text => {
          this.liveTurn().notices.push(
            (text || '').trim() || 'Sunucu bağlantısı beklenmedik şekilde kesildi.',
          );
          this.settle();
          this.scrollSoon();
        },
      },
      this.modelChoice,
      fileIds,
      this.approvalMode,
    );
  }

  /**
   * Switching modes. The switch switches — it does not open anything.
   *
   * The first version put a confirmation card in the way, and the first person
   * to use it pressed Auto, saw the mode stay on Manual, and was still asked
   * about every write. A control that needs a second control to take effect is
   * a control that reads as broken, and the operator's conclusion was that the
   * feature did not work rather than that they had missed a step.
   *
   * What that confirmation was for is real and has moved into the warning strip
   * shown for as long as Auto is live: this removes the one structural defence
   * against a tool result triggering a write on its own (§5.7) — every synopsis
   * the agent reads from a metadata provider is untrusted text, and the writes
   * on this surface delete video from disk and suspend subscribers. A standing
   * banner states that whenever it applies, which a modal answered once does
   * not.
   */
  setApprovalMode(mode: 'manual' | 'auto'): void {
    this.approvalMode = mode;
  }

  /** An upload still in flight. Sending now would name an id nobody has. */
  get uploading(): boolean {
    return this.attached.some(f => f.state === 'uploading');
  }

  stop(): void {
    if (this.stream) {
      this.stream.abort();
    }
  }

  /* ---- Attachments ------------------------------------------------------- */

  /**
   * What the panel accepts, and what it tells the file picker to offer.
   *
   * Deliberately not `*`: a format the server cannot parse becomes a chip that
   * says "OKUNAMADI", and the place to prevent that is the picker rather than
   * an error after the upload.
   */
  readonly acceptedFiles =
    '.xlsx,.xlsm,.xls,.csv,.tsv,.json,.pdf,.txt,.md,.png,.jpg,.jpeg,.webp,.gif';

  /** 12 MB, the same ceiling the service enforces. Checked here to fail fast. */
  private static readonly MAX_FILE_BYTES = 12 * 1024 * 1024;

  /** Human-sized file size for the download chip. */
  fileSize(file: ResultFile): string {
    if (file.sizeBytes > 1048576) {
      return (file.sizeBytes / 1048576).toFixed(1) + ' MB';
    }
    return Math.max(1, Math.round(file.sizeBytes / 1024)) + ' KB';
  }

  /**
   * Save a file the assistant built.
   *
   * Errors surface on the chip rather than as a thrown promise: a failed
   * download is not a failed turn, and the answer above it still stands.
   */
  saveFile(file: ResultFile): void {
    this.downloadError = '';
    this.agent.downloadFile(file.id, file.name).catch((err: Error) => {
      this.zone.run(() => {
        this.downloadError = err.message;
      });
    });
  }

  /**
   * Put the pictures from one tool result on the turn and start fetching them.
   *
   * The placeholder goes on immediately and the bytes fill it in when they
   * arrive, so the transcript does not jump as each poster lands. Each object URL
   * is remembered for ngOnDestroy — the browser will not reclaim a blob while a
   * URL to it exists, and a long session previewing artwork holds every one of
   * them otherwise.
   */
  private showImages(turn: ChatTurn, images: ResultImage[]): void {
    for (const meta of images) {
      if (turn.images.some(i => i.meta.id === meta.id)) {
        continue;
      }
      const shown: ShownImage = {meta, url: null, src: null, failed: false};
      turn.images = turn.images.concat([shown]);
      this.agent
        .loadImage(meta.id)
        .then(url => {
          this.zone.run(() => {
            this.objectUrls.push(url);
            shown.url = url;
            shown.src = this.sanitizer.bypassSecurityTrustUrl(url);
          });
        })
        .catch(() => {
          this.zone.run(() => {
            shown.failed = true;
          });
        });
    }
  }

  /**
   * Open a preview at its own size, in a tab of its own.
   *
   * A lightbox was the alternative and it is the wrong one here: the operator is
   * comparing this crop against the CMS screen behind the panel, and a modal
   * covers exactly what they are comparing it with.
   */
  openImage(image: ShownImage): void {
    if (image.url) {
      window.open(image.url, '_blank');
    }
  }

  /** Object URLs handed out by loadImage, revoked when the panel goes. */
  private objectUrls: string[] = [];

  /** Set when the last download attempt failed. */
  downloadError = '';

  pickFiles(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.attach(input.files);
    // Cleared so picking the same file twice in a row still fires `change`.
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    if (!event.dataTransfer || event.dataTransfer.types.indexOf('Files') < 0) {
      return;
    }
    event.preventDefault();
    this.dragging = true;
  }

  onDragLeave(event: DragEvent): void {
    // relatedTarget is null when the pointer leaves the window entirely; a move
    // between two children of the panel fires leave/enter and must not flicker.
    if (!event.relatedTarget) {
      this.dragging = false;
    }
  }

  onDrop(event: DragEvent): void {
    if (!event.dataTransfer) {
      return;
    }
    event.preventDefault();
    this.dragging = false;
    this.attach(event.dataTransfer.files);
  }

  /**
   * A screenshot pasted into the composer.
   *
   * The reason this exists: the poster the operator wants to upload is usually
   * on their screen, not in a folder. Pasting is one step, saving it out and
   * finding it again is four.
   */
  onPaste(event: ClipboardEvent): void {
    const items = event.clipboardData ? event.clipboardData.files : null;
    if (items && items.length) {
      event.preventDefault();
      this.attach(items);
    }
  }

  removeAttachment(file: PendingFile): void {
    this.attached = this.attached.filter(a => a.key !== file.key);
  }

  attachmentLabel(file: PendingFile): string {
    if (file.state === 'uploading') {
      return 'yükleniyor…';
    }
    if (file.state === 'failed') {
      return file.problem;
    }
    return file.detail;
  }

  private attach(files: FileList | null): void {
    if (!files) {
      return;
    }
    for (let i = 0; i < files.length; i++) {
      this.upload(files[i]);
    }
  }

  private upload(file: File): void {
    const entry: PendingFile = {
      key: 'f' + this.fileCounter++,
      name: file.name,
      sizeBytes: file.size,
      state: 'uploading',
      id: '',
      kind: '',
      detail: '',
      problem: '',
    };
    this.attached.push(entry);

    if (file.size > AgentChatComponent.MAX_FILE_BYTES) {
      entry.state = 'failed';
      entry.problem = 'Dosya 12 MB sınırını aşıyor';
      return;
    }

    this.agent
      .uploadFile(file, this.sessionId, this.provider, this.modelChoice)
      .then(result => {
        this.zone.run(() => {
          // The upload is what opened the conversation when there was none.
          if (!this.sessionId && result.sessionId) {
            this.sessionId = result.sessionId;
          }
          entry.id = result.id;
          entry.kind = result.kind;
          if (result.problem) {
            entry.state = 'failed';
            entry.problem = result.problem;
            return;
          }
          entry.state = 'ready';
          entry.detail = this.describeFile(result);
        });
      })
      .catch((err: Error) => {
        this.zone.run(() => {
          entry.state = 'failed';
          entry.problem = err.message || 'Yüklenemedi';
        });
      });
  }

  /**
   * The chip's second line — what the file turned out to hold.
   *
   * Every figure here came from the server actually opening the file. A row
   * count guessed from the size, or a sheet count assumed from the extension,
   * would be the panel making a claim it cannot support.
   */
  private describeFile(file: AgentFile): string {
    if (file.kind === 'image') {
      return file.width ? file.width + '×' + file.height + ' px' : 'görsel';
    }
    if (!file.tables.length) {
      return Math.max(1, Math.round(file.sizeBytes / 1024)) + ' KB';
    }
    const rows = file.tables.reduce((sum, t) => sum + t.rowCount, 0);
    const sheets =
      file.tables.length > 1 ? file.tables.length + ' sayfa · ' : '';
    return sheets + rows.toLocaleString('tr') + ' satır';
  }

  /** Enter sends; Shift+Enter and the menus get first refusal. */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeMenus();
      return;
    }
    const rows = this.menuRows();
    if (this.menuOpen && rows.length) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : rows.length - 1;
        this.active = (this.active + step) % rows.length;
        this.revealActiveRow();
        return;
      }
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.active = event.key === 'Home' ? 0 : rows.length - 1;
        this.revealActiveRow();
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        this.pick(rows[this.active]);
        return;
      }
    }
    // Below the menu block on purpose: while a menu is open Tab belongs to it,
    // and that branch has already returned by here.
    if (event.key === 'Tab' && !event.shiftKey && this.showSuggestion()) {
      event.preventDefault();
      this.acceptSuggestion();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  /**
   * Keep the arrowed-to row inside the menu's scroll box.
   *
   * The menu is capped and scrolls, so on a long list the selection could walk
   * off the bottom and Enter would then run a row nobody could see. After the
   * next tick, because the class that marks it moves with change detection.
   */
  private revealActiveRow(): void {
    setTimeout(() => {
      if (!this.menuEl || !this.menuEl.nativeElement) {
        return;
      }
      const row = this.menuEl.nativeElement.querySelector('.agent-menu-row.active');
      if (row && (row as any).scrollIntoView) {
        (row as HTMLElement).scrollIntoView({block: 'nearest'});
      }
    }, 0);
  }

  /**
   * The "@" and "/" menus open off the token under the caret, so they follow
   * what is being typed rather than needing a button.
   */
  onDraftChange(): void {
    this.autoGrow();
    // Typing while the mic is on wins: the typed text becomes the new base, so
    // the next interim phrase extends it instead of overwriting it. ngModel
    // only emits this on real input, so a dictated update cannot land here.
    if (this.wantDictation) {
      this.dictated = this.draft;
    }
    const token = this.token();
    if (token) {
      this.menuKind = token.sigil;
      this.menuOpen = true;
      this.active = 0;
      this.modelMenuOpen = false;
    } else if (this.menuKind !== 'plus') {
      this.menuOpen = false;
      this.menuKind = '';
    }
  }

  /** The trailing "@…" or "/…" the caret is inside, if any. */
  private token(): {sigil: string; query: string; start: number} | null {
    const match = /(^|\s)([@\/])([^\s@\/]*)$/.exec(this.draft);
    if (!match) {
      return null;
    }
    return {
      sigil: match[2] === '@' ? 'at' : 'slash',
      query: AgentChatComponent.fold(match[3]),
      start: this.draft.length - match[3].length - 1,
    };
  }

  /**
   * Lowercase, with Turkish letters folded to their ASCII neighbours.
   *
   * Without this the menu was unusable for exactly the rows an operator is most
   * likely to want. `toLowerCase()` maps "I" to "i" but leaves "ı" alone, so
   * typing `@kilavuz` — which is what anyone types who is not going to hunt for
   * ı on the keyboard — scored zero against "@kılavuz" and the row vanished.
   * Same for "içerik", "bağlantı", "hafıza".
   *
   * Folding BOTH sides means it also works the other way: typing the correct
   * "ı" still matches, because both collapse to the same string.
   */
  private static fold(value: string): string {
    let out = value.toLowerCase();
    const from = 'ıİişŞğĞüÜöÖçÇ';
    const to = 'iiissgguuoocc';
    let folded = '';
    for (let i = 0; i < out.length; i++) {
      const at = from.indexOf(out.charAt(i));
      folded += at === -1 ? out.charAt(i) : to.charAt(at);
    }
    return folded;
  }

  menuRows(): MenuRow[] {
    if (!this.menuOpen) {
      return [];
    }
    // The "+" menu is for what the button is: adding something to the message.
    // Scopes and commands are NOT listed here — they belong to the sigils that
    // summon them, and duplicating them into a button menu is how an operator
    // learns to hunt for a list instead of typing "@". Same division as the
    // reference bar, where "+" opens sources and "/" opens commands.
    if (this.menuKind === 'plus') {
      return [FILE_ROW].concat(SIGIL_ROWS);
    }
    const source = this.menuKind === 'at' ? SCOPES : COMMANDS;
    const token = this.token();
    if (!token || !token.query) {
      return source;
    }

    // Ranked, not filtered. A plain substring test put "@katalog" above
    // "@kanal" for the query "kan" because it ran in table order, so the row
    // the operator was spelling out was never the one Enter would take.
    //
    // The hint is searched too: "epg" and "catch-up" appear nowhere in
    // "@kanal", and an operator who knows what they want by its own name
    // should not have to know ours first.
    const scored: {row: MenuRow; rank: number}[] = [];
    for (let i = 0; i < source.length; i++) {
      const row = source[i];
      // The sigil is not part of what is being typed — the query starts after
      // it — so it must come off the name before either is compared.
      const name = AgentChatComponent.fold(row.name.substring(1));
      const hint = AgentChatComponent.fold(row.hint);
      let rank = -1;
      if (name.indexOf(token.query) === 0) {
        rank = 0;
      } else if (name.indexOf(token.query) > 0) {
        rank = 1;
      } else if (hint.indexOf(token.query) > -1) {
        rank = 2;
      }
      if (rank > -1) {
        scored.push({row: row, rank: rank});
      }
    }
    // Stable within a rank: table order is the considered order, and shuffling
    // equally-good rows on each keystroke moves the target under the cursor.
    scored.sort((a, b) => a.rank - b.rank);
    return scored.map(entry => entry.row);
  }

  menuQuery(): string {
    const token = this.token();
    return token ? token.query : '';
  }

  pick(row: MenuRow): void {
    if (!row) {
      return;
    }

    // The typed sigil and query come off the draft first, whatever the row
    // does. Leaving "/dos" in the box behind an opened file dialog was the one
    // way this menu could put text into a message that nobody typed on purpose.
    const token = this.token();
    const head = token ? this.draft.substring(0, token.start) : this.draft;

    if (row.raw) {
      // Types the character and steps aside: onDraftChange sees the new token
      // and opens the menu that belongs to it.
      this.draft = head + row.insert;
      this.focusInput();
      this.onDraftChange();
      return;
    }

    if (row.action) {
      this.draft = head;
      this.closeMenus();
      this.runAction(row.action);
      this.focusInput();
      setTimeout(() => this.autoGrow(), 0);
      return;
    }

    if (row.insert) {
      this.draft = head + row.insert + ' ';
    } else {
      // A command row runs now rather than typing itself into the box: the
      // operator picked the action, not the text.
      this.draft = head;
      this.runCommand(row.name);
    }
    this.closeMenus();
    this.focusInput();
    setTimeout(() => this.autoGrow(), 0);
  }

  /** What a row with an `action` does. Each is a control the panel already has. */
  private runAction(action: string): void {
    if (action === 'files') {
      this.pickFiles();
    } else if (action === 'connect') {
      this.connectOpen = false;
      this.toggleConnect();
    } else if (action === 'notes') {
      this.notesOpen = false;
      this.toggleNotes();
    } else if (action === 'manual' || action === 'auto') {
      this.setApprovalMode(action === 'auto' ? 'auto' : 'manual');
    }
  }

  /**
   * The line under the menu.
   *
   * For "+" it names the two sigils. That menu no longer lists scopes or
   * commands, and a feature reachable only by typing a character nobody
   * mentioned is a feature most operators never find.
   */
  menuFoot(): string {
    if (this.menuKind === 'plus') {
      return 'Kapsam için @, komut için / yazın';
    }
    return this.menuKind === 'at'
      ? '↑↓ gez · ↵ seç — mesaja bir kapsam sözcüğü ekler'
      : '↑↓ gez · ↵ çalıştır';
  }


  togglePlus(): void {
    this.modelMenuOpen = false;
    if (this.menuOpen && this.menuKind === 'plus') {
      this.closeMenus();
      return;
    }
    this.menuOpen = true;
    this.menuKind = 'plus';
    this.active = 0;
  }

  closeMenus(): void {
    this.menuOpen = false;
    this.menuKind = '';
    this.modelMenuOpen = false;
  }

  /** True when the message was a panel command and has been carried out. */
  private runCommand(message: string): boolean {
    const word = message.trim().toLowerCase();
    if (word === '/yeni') {
      this.newSession();
      this.draft = '';
      return true;
    }
    if (word === '/durdur') {
      this.stop();
      this.draft = '';
      return true;
    }
    if (word === '/dosya') {
      this.draft = '';
      this.pickFiles();
      return true;
    }
    if (word === '/manual' || word === '/auto') {
      this.draft = '';
      this.setApprovalMode(word === '/auto' ? 'auto' : 'manual');
      return true;
    }
    if (word === '/bağlantı' || word === '/baglanti') {
      this.draft = '';
      this.connectOpen = false;
      this.toggleConnect();
      return true;
    }
    if (word === '/hafıza' || word === '/hafiza' || word === '/notlar') {
      this.draft = '';
      this.notesOpen = false;
      this.toggleNotes();
      return true;
    }
    return false;
  }

  toggleMore(): void {
    this.moreOpen = !this.moreOpen;
  }

  dismissDisclaimer(): void {
    this.disclaimerOpen = false;
    this.writeStore(DISCLAIMER_KEY, DISCLAIMER_VERSION);
  }

  /**
   * Put the notice back, and forget that it was ever dismissed.
   *
   * Clearing the mark rather than only flipping the flag: an operator asking to
   * see it again has withdrawn the dismissal, and leaving the stored version in
   * place would hide it again on the next reload — which looks like the menu
   * row did not work.
   */
  showDisclaimer(): void {
    this.moreOpen = false;
    this.disclaimerOpen = true;
    this.clearStore(DISCLAIMER_KEY);
  }

  /**
   * localStorage, guarded.
   *
   * Both directions throw for real reasons — Safari's private mode has a
   * zero-quota store that rejects every write, and an admin screen embedded in
   * a cross-origin frame gets a SecurityError on access. Neither is a reason
   * for the panel to fail to mount, and the worst outcome of swallowing them is
   * a dismissed notice that comes back.
   */
  private readStore(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  private writeStore(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // Dismissed for this session only; see readStore().
    }
  }

  private clearStore(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Nothing was stored to begin with; see readStore().
    }
  }

  /* ---- Model picker ------------------------------------------------------
     Parked. The picker is not rendered — the session runs on whatever model the
     service reports as the default — but everything below still works and the
     markup is kept in the template behind a comment, so putting the chip back
     is uncommenting two blocks and re-listing /model in COMMANDS. */

  toggleModelMenu(): void {
    this.menuOpen = false;
    this.modelMenuOpen = !this.modelMenuOpen;
    this.modelQuery = '';
    if (this.modelMenuOpen) {
      this.loadModels();
    }
  }

  private loadModels(): void {
    if (!this.provider) {
      return;
    }
    this.agent.models(this.provider).then(
      list => {
        const models = list.models || [];
        // Normalised rather than trusted: the two providers report different
        // shapes, and a row rendered straight from the payload printed
        // "[object Object]" for every model the router offered.
        this.providerModels = models
          .map((entry: any) =>
            typeof entry === 'string' ? {name: entry} : entry,
          )
          .filter(entry => entry && entry.name);
        if (!this.modelChoice && list.configured) {
          this.modelChoice = list.configured;
        }
      },
      () => {
        this.providerModels = [];
      },
    );
  }

  /** What a model row reads as: the provider's own display name if it has one. */
  modelName(model: AgentModel): string {
    return model.displayName || model.name;
  }

  /**
   * The model list, filtered. A router gateway offers several hundred models,
   * and picking one out of an unfiltered list is not a thing anyone can do.
   */
  filteredModels(): AgentModel[] {
    const query = this.modelQuery.trim().toLowerCase();
    if (!query) {
      return this.providerModels;
    }
    return this.providerModels.filter(
      model =>
        model.name.toLowerCase().indexOf(query) > -1 ||
        (model.displayName || '').toLowerCase().indexOf(query) > -1,
    );
  }

  /**
   * Switching provider or model starts a new conversation.
   *
   * The service holds one provider instance per session and that instance owns
   * the history, so a switch cannot carry the transcript with it. Clearing the
   * panel is the honest rendering: leaving the old turns on screen would show a
   * conversation the new model has never seen.
   */
  chooseProvider(name: string): void {
    if (name === this.provider) {
      this.closeMenus();
      return;
    }
    this.provider = name;
    this.modelChoice = '';
    this.providerModels = [];
    this.newSession();
    this.loadModels();
    this.closeMenus();
  }

  chooseModel(name: string): void {
    if (name === this.modelChoice) {
      this.closeMenus();
      return;
    }
    this.modelChoice = name;
    this.newSession();
    this.closeMenus();
    // The chip is one of the controls the threshold is derived from, so a
    // longer name can be what tips the draft onto its own line.
    setTimeout(() => this.autoGrow(), 0);
  }

  /**
   * The model chip's text.
   *
   * The vendor prefix is dropped — "google/gemma-4-26b-a4b-qat" becomes
   * "gemma-4-26b-a4b-qat" — because it is the same for every model in a given
   * list and identifies nothing, while the composer is 440px wide and the chip
   * sits beside the box the operator is trying to type in. The full id is still
   * on the button's title, and the picker lists it in full.
   */
  modelLabel(): string {
    const full = this.model || this.modelChoice || this.provider || 'model';
    const slash = full.lastIndexOf('/');
    return slash >= 0 ? full.substring(slash + 1) : full;
  }

  /** The unshortened id, for the title and the picker. */
  modelFull(): string {
    return this.model || this.modelChoice || this.provider || 'model';
  }

  /* ---- Notes ------------------------------------------------------------- */

  /**
   * What the assistant carries into future sessions.
   *
   * Fetched rather than accumulated from the stream: the stream only ever shows
   * what was learned in this browser session, and the point of the list is
   * everything it has learned in all of them.
   */
  toggleNotes(): void {
    this.moreOpen = false;
    this.notesOpen = !this.notesOpen;
    if (this.notesOpen) {
      this.connectOpen = false;
    }
    if (this.notesOpen && !this.notes.length) {
      this.loadNotes();
    }
  }

  /**
   * The same tools, in the operator's own AI client.
   *
   * The panel is not the only reasonable place to use them — somebody living in
   * a terminal wants them there — and the MCP server takes the operator's token
   * per call, so lending it to another client changes nothing about who the
   * work is done as. See atlas-mcp-server's remote binding.
   */
  toggleConnect(): void {
    this.moreOpen = false;
    this.connectOpen = !this.connectOpen;
    if (this.connectOpen) {
      this.notesOpen = false;
      this.connection = this.agent.mcpConnection();
      this.copiedField = '';
      // Never carried over from the last time the dialog was open: a token left
      // on screen outlives the reason it was shown.
      this.connectRevealed = false;
    }
  }

  /** Both dialogs answer to the same dismissals — scrim, Escape, the X. */
  closeSheets(): void {
    this.notesOpen = false;
    this.connectOpen = false;
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKey(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }
    if (this.notesOpen || this.connectOpen) {
      this.closeSheets();
    } else if (this.moreOpen) {
      this.moreOpen = false;
    }
  }

  /** What goes on screen — the placeholder, or the real thing on request. */
  tokenText(): string {
    if (!this.connection) {
      return '';
    }
    return this.connectRevealed ? this.connection.token : this.connection.masked;
  }

  /**
   * Copy the real snippet, token and all.
   *
   * `navigator.clipboard` needs a secure context, and the admin UI is served
   * over plain HTTP on the internal hosts, so the old execCommand path is not
   * legacy politeness — it is the one that runs there.
   */
  /**
   * Copy one field, and say which one was copied.
   *
   * The token is always taken from the connection rather than from what is on
   * screen: masked is the resting state, and copying the placeholder would hand
   * over a string of Turkish in angle quotes that fails inside the client's
   * HTTP library — see mcpConnection().
   */
  copyField(field: 'url' | 'token'): void {
    if (!this.connection) {
      return;
    }
    const text = field === 'url' ? this.connection.url : this.connection.token;
    const done = () => {
      this.copiedField = field;
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        this.zone.run(() => (this.copiedField = ''));
      }, 2000);
    };

    const clipboard = (navigator as any).clipboard;
    if (clipboard && clipboard.writeText) {
      clipboard.writeText(text).then(done, () => this.copyFallback(text, done));
      return;
    }
    this.copyFallback(text, done);
  }


  private copyFallback(text: string, done: () => void): void {
    const area = document.createElement('textarea');
    area.value = text;
    // Off-screen rather than hidden: display:none is not selectable, and the
    // copy silently does nothing.
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    area.select();
    try {
      if (document.execCommand('copy')) {
        done();
      }
    } catch (e) {
      // Nothing to offer but the text on screen, which is masked — so say so
      // rather than pretending the copy worked.
      this.copiedField = '';
    }
    document.body.removeChild(area);
  }

  private loadNotes(): void {
    this.notesLoading = true;
    this.agent.notes().then(
      notes => {
        this.notes = notes;
        this.notesLoading = false;
      },
      () => {
        this.notes = [];
        this.notesLoading = false;
      },
    );
  }

  /**
   * Drop a note. Removed from the panel immediately and from the transcript
   * rows too — a row still reading "not alındı" after the note has gone would
   * be the panel lying about what the assistant knows.
   */
  forget(note: AgentNote): void {
    this.agent.forget(note.id);
    this.notes = this.notes.filter(n => n.id !== note.id);
    for (let i = 0; i < this.turns.length; i++) {
      this.turns[i].notes = this.turns[i].notes.filter(n => n.id !== note.id);
    }
  }

  noteScopeLabel(scope: string): string {
    if (scope === 'preference') {
      return 'tercih';
    }
    if (scope === 'correction') {
      return 'düzeltme';
    }
    return 'platform';
  }

  /* ---- Dictation --------------------------------------------------------- */

  get canDictate(): boolean {
    const w = window as any;
    return Boolean(w.SpeechRecognition || w.webkitSpeechRecognition);
  }

  toggleDictation(): void {
    if (this.wantDictation) {
      this.stopDictation();
      return;
    }
    // Everything typed before dictation started is kept: speech is appended to
    // the message, it does not replace it.
    this.dictated = this.draft;
    this.wantDictation = true;
    this.startRecognition();
  }

  /**
   * Append a spoken phrase, with exactly one space between phrases.
   *
   * Chrome usually prefixes a continuation with a space and sometimes does not,
   * which varies by platform and language. Normalising here is why "üç tane
   * test kullanıcısı" and "üst paket" do not arrive fused into one word.
   */
  private joinSpeech(head: string, tail: string): string {
    const text = tail.replace(/^\s+/, '');
    if (!text) {
      return head;
    }
    if (!head) {
      return text;
    }
    return /\s$/.test(head) ? head + text : head + ' ' + text;
  }

  private startRecognition(): void {
    const w = window as any;
    const Recognition = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!Recognition) {
      this.wantDictation = false;
      return;
    }
    // Each session has its own results list, so the committed count restarts
    // with it. Carrying it over would silently skip the new session's first
    // phrases.
    this.finals = 0;
    const recognition = new Recognition();
    recognition.lang = 'tr-TR';
    recognition.interimResults = true;
    recognition.continuous = true;

    /**
     * Zone.js does not patch SpeechRecognition, so its callbacks land outside
     * Angular. Without zone.run() the draft was updated correctly and simply
     * never rendered — the words appeared all at once on the next event the
     * framework did know about, which was the operator pressing stop.
     */
    recognition.onresult = (event: any) => {
      this.zone.run(() => {
        /**
         * A final result is committed to `dictated` once; interim results are
         * only ever appended to it for display and recomputed on every event.
         * Appending everything straight to `draft` as it arrived re-added each
         * interim phrase, so a sentence accumulated a stutter of itself while
         * it was being spoken.
         *
         * The loop starts at `finals`, not at `event.resultIndex`. Chrome is
         * supposed to advance resultIndex past settled results, but under
         * `continuous` it can keep reporting 0 while the list grows, which
         * would commit the same phrase again on every event. Counting what has
         * been committed is true whichever way the browser behaves.
         */
        let interim = '';
        for (let i = this.finals; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            this.dictated = this.joinSpeech(this.dictated, result[0].transcript);
            this.finals = i + 1;
          } else {
            interim = this.joinSpeech(interim, result[0].transcript);
          }
        }
        this.draft = this.joinSpeech(this.dictated, interim);
        this.autoGrow();
      });
    };

    recognition.onerror = (event: any) => {
      // A pause in speech is not a failure — the recogniser ends the session
      // and onend restarts it. Anything else stops dictation for good, because
      // restarting into the same error is a loop the operator cannot break.
      if (event && event.error === 'no-speech') {
        return;
      }
      this.zone.run(() => this.stopDictation());
    };

    recognition.onend = () => {
      this.zone.run(() => {
        this.recognition = undefined;
        // Chrome ends a "continuous" session on its own after a silence. While
        // the operator still has the mic switched on, that is a restart, not a
        // stop — otherwise dictation dies mid-sentence with the button still lit.
        if (this.wantDictation) {
          this.startRecognition();
          return;
        }
        this.listening = false;
      });
    };

    try {
      recognition.start();
    } catch (e) {
      // start() throws if a previous session has not fully ended yet.
      this.wantDictation = false;
      this.listening = false;
      return;
    }
    this.recognition = recognition;
    this.listening = true;
  }

  private stopDictation(): void {
    this.wantDictation = false;
    if (this.recognition) {
      this.recognition.stop();
      this.recognition = undefined;
    }
    this.listening = false;
  }

  /* ---- Cards ------------------------------------------------------------- */

  question(card: PendingCard): AskQuestion {
    return card.questions[card.index];
  }

  isPicked(card: PendingCard, option: number): boolean {
    const chosen = card.picked[this.question(card).id] || [];
    return chosen.indexOf(option) > -1;
  }

  choose(card: PendingCard, option: number): void {
    const question = this.question(card);
    const chosen = card.picked[question.id] || [];
    if (question.multiple) {
      card.picked[question.id] =
        chosen.indexOf(option) > -1 ? chosen.filter(i => i !== option) : chosen.concat([option]);
      return;
    }
    card.picked[question.id] = [option];
    card.custom[question.id] = '';
    // Single choice advances itself, as the primitive does: the pause is long
    // enough to see the dot land and short enough not to feel like a wait.
    setTimeout(() => {
      if (card.sent) {
        return;
      }
      if (card.index === card.questions.length - 1) {
        this.submit(card);
      } else {
        card.index += 1;
      }
    }, 480);
  }

  onCustom(card: PendingCard): void {
    const question = this.question(card);
    if (!question.multiple && (card.custom[question.id] || '').trim()) {
      card.picked[question.id] = [];
    }
  }

  hasAnswer(card: PendingCard): boolean {
    const question = this.question(card);
    const chosen = card.picked[question.id] || [];
    return chosen.length > 0 || (card.custom[question.id] || '').trim().length > 0;
  }

  goto(card: PendingCard, index: number): void {
    if (!card.sent) {
      card.index = index;
    }
  }

  advance(card: PendingCard): void {
    if (card.index === card.questions.length - 1) {
      this.submit(card);
    } else {
      card.index += 1;
    }
  }

  /** Every answer the operator gave, as the strings they actually chose. */
  private answersOf(card: PendingCard): {[id: string]: string[]} {
    const answers: {[id: string]: string[]} = {};
    for (let i = 0; i < card.questions.length; i++) {
      const question = card.questions[i];
      const chosen = (card.picked[question.id] || []).map(index => question.options[index]);
      const typed = (card.custom[question.id] || '').trim();
      answers[question.id] = typed ? chosen.concat([typed]) : chosen;
    }
    return answers;
  }

  submit(card: PendingCard): void {
    if (card.sent) {
      return;
    }
    card.sent = true;
    const answers = this.answersOf(card);
    if (card.kind === 'approve') {
      // One question, two options; index 0 is "Onayla" by construction.
      const approved = (card.picked[card.questions[0].id] || []).indexOf(0) > -1;
      this.agent.approve(this.sessionId, card.callId, approved);
    } else {
      this.agent.respond(this.sessionId, card.callId, answers);
    }
    // The card stays on screen in its sent state for a moment so the answer is
    // visible next to what was asked, then leaves with the rest of the trace.
    setTimeout(() => {
      this.pending = this.pending.filter(p => p.callId !== card.callId);
    }, 900);
  }

  /** Declining is one press, not a pick-then-send. */
  decline(card: PendingCard): void {
    if (card.sent) {
      return;
    }
    card.sent = true;
    card.picked[card.questions[0].id] = [1];
    this.agent.approve(this.sessionId, card.callId, false);
    setTimeout(() => {
      this.pending = this.pending.filter(p => p.callId !== card.callId);
    }, 900);
  }

  toggleArgs(card: PendingCard): void {
    card.argsOpen = !card.argsOpen;
  }

  /* ---- Session ----------------------------------------------------------- */

  /**
   * Draw the chips the empty panel offers.
   *
   * A partial Fisher–Yates over a copy: it takes only the four swaps it needs
   * rather than shuffling ninety-odd strings, and drawing by index means the
   * same question cannot appear twice in one set — which a naive
   * `pool[random()]` per slot does often enough to look broken.
   */
  rollStarters(): void {
    const pool = STARTER_POOL.slice();
    const picked: string[] = [];
    const take = Math.min(STARTER_COUNT, pool.length);
    for (let i = 0; i < take; i++) {
      const j = i + Math.floor(Math.random() * (pool.length - i));
      const swap = pool[i];
      pool[i] = pool[j];
      pool[j] = swap;
      picked.push(pool[i]);
    }
    this.starters = picked;
  }

  newSession(): void {
    this.stop();
    // A new session gets a new set: the empty panel is about to be looked at
    // again, and the four it was showing are the four already seen. The ghost
    // goes with them — it was a follow-up to a conversation that no longer
    // exists.
    this.rollStarters();
    this.drawSuggestion();
    this.sessionId = '';
    this.model = '';
    this.turns = [];
    this.pending = [];
    // The service drops the files with the session; the tray must not keep
    // chips whose ids no longer resolve.
    this.attached = [];
  }

  /* ---- Stream ------------------------------------------------------------ */

  private apply(event: AgentEvent): void {
    switch (event.type) {
      case 'session':
        this.sessionId = event.sessionId;
        this.model = event.model;
        break;

      case 'text_delta':
        this.appendAssistant(event.text);
        break;

      case 'reasoning_delta':
        this.appendReasoning(event.text);
        break;

      case 'tool_start': {
        const turn = this.liveTurn();
        // Reasoning stops when the model commits to an action.
        this.closeReasoningClock(turn);
        const activity = this.newActivity(event);
        // A write that reached tool_start was approved, so it belongs with the
        // things that changed something rather than with the lookups.
        if (event.kind === 'write') {
          turn.tasks.push(activity);
        } else {
          turn.tools.push(activity);
        }
        break;
      }

      case 'tool_result': {
        const turn = this.liveTurn();
        const entry = this.findActivity(turn, event.callId);
        if (entry) {
          entry.state = event.ok ? 'ok' : 'failed';
          entry.summary = event.summary || '';
          entry.table = event.table || null;
          // A failure is the one case worth opening unprompted: it is why the
          // answer above it may be wrong.
          if (!event.ok) {
            entry.open = true;
            // Once, and it counts as the panel having had its say: a failure is
            // worth surfacing over a closed trace, but not repeatedly.
            turn.traceOpen = true;
            turn.traceTouched = true;
          }
        }
        if (event.docs && event.docs.length) {
          turn.docs = turn.docs.concat(event.docs);
        }
        if (event.file && !turn.files.some(f => f.id === event.file!.id)) {
          turn.files = turn.files.concat([event.file]);
        }
        if (event.images && event.images.length) {
          this.showImages(turn, event.images);
        }
        break;
      }

      case 'approval_request':
        if (event.auto) {
          // Nothing is waiting on this one. Pushing it to `pending` would flash
          // an actionable card on screen and remove it a moment later, which
          // reads as an approval the operator missed rather than one the mode
          // had already given.
          this.liveTurn().autoWrites.push({
            tool: event.tool,
            preview: (event.preview || '').trim() || this.shortName(event.tool),
          });
        } else {
          this.pending.push(this.approvalCard(event));
        }
        break;

      case 'ask':
        // The service will not emit a question with nothing in it, but a card
        // with no questions would throw on its first render rather than degrade,
        // so it is never built.
        if (event.questions && event.questions.length) {
          this.pending.push(this.askCard(event));
        }
        break;

      case 'approval_resolved':
      case 'ask_resolved':
        this.pending = this.pending.filter(p => p.callId !== event.callId);
        break;

      case 'followups':
        this.liveTurn().followups = event.items || [];
        break;

      case 'text_trim': {
        /**
         * The service reshaping something it already sent — today, follow-up
         * questions the model wrote as prose being lifted out and rendered as
         * buttons instead.
         *
         * The suffix is matched rather than trusted as a length: if the answer
         * does not actually end with it, nothing is cut. A mis-trimmed reply is
         * worse than a duplicated list.
         */
        const turn = this.liveTurn();
        const suffix = event.suffix || '';
        if (suffix && turn.text.endsWith(suffix)) {
          turn.text = turn.text.slice(0, turn.text.length - suffix.length);
          // The tail streams as word spans and the settled part is Markdown;
          // both are rebuilt from turn.text when the turn settles.
          turn.tail = [];
          turn.settledHtml = renderMarkdown(turn.text);
        }
        break;
      }

      case 'ungrounded':
        this.liveTurn().ungrounded = event.figures || [];
        break;

      case 'note_saved':
        this.liveTurn().notes.push({
          id: event.id,
          scope: event.scope,
          text: event.text,
          createdAt: '',
        });
        // The stored list on screen is now behind by one.
        this.notes = [];
        break;

      case 'error':
        this.liveTurn().notices.push(
          (event.message || '').trim() ||
            'Tur bir hatayla bitti, ancak sunucu bir açıklama göndermedi. ' +
              'Son işlemin gerçekleşip gerçekleşmediğini kontrol edin.',
        );
        break;
    }
    this.scrollSoon();
  }

  private approvalCard(event: AgentEvent): PendingCard {
    const label = this.shortName(event.tool);
    return {
      kind: 'approve',
      callId: event.callId,
      title: 'Bu işlem için onayınız gerekiyor',
      tool: label,
      preview: event.preview || '',
      changes: event.changes || [],
      args: event.args ? this.pretty(event.args) : '',
      argsOpen: false,
      questions: [
        {
          id: 'approve',
          text: event.preview || label + ' çalıştırılsın mı?',
          options: ['Onayla', 'Reddet'],
          multiple: false,
          allowCustom: false,
        },
      ],
      index: 0,
      picked: {},
      custom: {},
      sent: false,
    };
  }

  private askCard(event: AgentEvent): PendingCard {
    return {
      kind: 'ask',
      callId: event.callId,
      title: event.title || 'Devam etmek için bir şey sormam gerekiyor',
      tool: '',
      preview: '',
      changes: [],
      args: '',
      argsOpen: false,
      questions: event.questions || [],
      index: 0,
      picked: {},
      custom: {},
      sent: false,
    };
  }

  private newActivity(event: AgentEvent): ToolActivity {
    const label = this.shortName(event.tool);
    return {
      callId: event.callId,
      tool: event.tool,
      label: label,
      family: this.familyOf(event.tool),
      kind: event.kind,
      args: this.short(event.args),
      argsFull: this.pretty(event.args),
      state: 'running',
      summary: '',
      open: false,
      table: null,
    };
  }

  private findActivity(turn: ChatTurn, callId: string): ToolActivity {
    const all = turn.tools.concat(turn.tasks);
    return all.filter(a => a.callId === callId)[0];
  }

  /* ---- Loading state ----------------------------------------------------- */


  /* ---- Trace ------------------------------------------------------------- */

  /**
   * The whole of the ungrounded warning, as a tooltip.
   *
   * Names the figures first. "Doğrulanamadı" on its own tells the operator to
   * distrust the reply without saying which part, and on a reply where most of
   * the numbers came straight from a query that is the wrong instruction — the
   * one that was reported was 63, the total of three figures the tool did
   * return.
   */
  ungroundedHint(turn: ChatTurn): string {
    const plural = turn.ungrounded.length > 1 ? 'Bu sayılar' : 'Bu sayı';
    return (
      plural + ' hiçbir araç sonucunda geçmiyor. Bir hesaplamadan türetilmiş ' +
      'olabilir (toplam, fark, yüzde) ya da model uydurmuş olabilir. Kritikse ' +
      'sorguyu yeniden çalıştırın.'
    );
  }

  toggleUngrounded(turn: ChatTurn): void {
    turn.ungroundedOpen = !turn.ungroundedOpen;
  }

  toggleTrace(turn: ChatTurn): void {
    turn.traceTouched = true;
    turn.traceOpen = !turn.traceOpen;
    // `traceTab` starts at 'steps' and nothing moves it any more, so a turn that
    // only ever reasoned — no tool calls — would open onto a tab it does not
    // have and show an empty rail. Snap to a tab the turn actually offers; with
    // one tab the strip is not even rendered, which makes an unreachable
    // selection impossible to correct by hand.
    const tabs = this.tabsOf(turn);
    if (turn.traceOpen && tabs.length && tabs.indexOf(turn.traceTab) === -1) {
      turn.traceTab = tabs[0];
    }
  }

  setTab(turn: ChatTurn, tab: string): void {
    // Picking a tab is opening it on purpose.
    turn.traceTouched = true;
    turn.traceTab = tab;
    turn.traceOpen = true;
  }

  /**
 * Which tabs this turn has anything to put in. Empty tabs are not offered.
 *
 * There used to be a fourth, "Sorgular", rendering the SQL the model wrote.
 * Nothing writes SQL any more — the Postgres connection is gone and subscriber
 * and package questions go through the crm_* API tools — so the tab could never
 * fill again.
 */
  tabsOf(turn: ChatTurn): string[] {
    const tabs: string[] = [];
    if (turn.tools.length || turn.tasks.length) {
      tabs.push('steps');
    }
    if (turn.reasoning) {
      tabs.push('reasoning');
    }
    if (turn.docs.length) {
      tabs.push('sources');
    }
    return tabs;
  }

  tabLabel(tab: string): string {
    if (tab === 'steps') {
      return 'Adımlar';
    }
    if (tab === 'reasoning') {
      return 'Akıl yürütme';
    }
    return 'Kaynaklar';
  }

  /**
   * The trace's own summary line, which is what it says when collapsed.
   *
   * This is now the only thing on a turn that says work is in flight, so it has
   * to keep moving. A live turn used to short-circuit to "Çalışıyor"; the
   * counts here update as it runs instead — reasoningMs is recomputed on every
   * reasoning delta and the tool list grows as calls start — so the running
   * label is the real one, filling in rather than being replaced when the turn
   * ends. The shimmer on the span carries the rest.
   */
  traceLabel(turn: ChatTurn): string {
    const calls = turn.tools.length + turn.tasks.length;
    const seconds = Math.round(turn.reasoningMs / 1000);
    if (calls && seconds) {
      return seconds + ' sn düşündü · ' + calls + ' araç çağrısı';
    }
    if (calls) {
      return calls + ' araç çağrısı';
    }
    if (seconds) {
      return seconds + ' saniye düşündü';
    }
    // Nothing to count yet — the first second of a turn that has opened the
    // trace to stream reasoning into it. Past tense would be wrong while it is
    // still arriving.
    return turn.live && this.busy ? 'Düşünüyor' : 'Düşündü';
  }


  toggleTool(tool: ToolActivity): void {
    tool.open = !tool.open;
  }

  /** A step's right-hand meta — the tool's own count, or its state. */
  stepMeta(tool: ToolActivity): string {
    if (tool.state === 'running') {
      return '';
    }
    if (tool.state === 'failed') {
      return 'hata';
    }
    if (tool.table) {
      return tool.table.rowCount + ' satır';
    }
    return '';
  }

  toneOf(tool: ToolActivity): string {
    const family = FAMILIES[tool.family];
    return family ? family.tone : 'grey';
  }

  familyLabel(name: string): string {
    const family = FAMILIES[this.familyOf(name)];
    return family ? family.label : this.familyOf(name);
  }

  familyTone(name: string): string {
    const family = FAMILIES[this.familyOf(name)];
    return family ? family.tone : 'grey';
  }

  /* ---- Answer ------------------------------------------------------------ */

  /**
   * Which tools fed this answer, deduplicated — the "sources" line under a
   * streamed reply. Derived from calls that actually returned, so it cannot
   * claim a source the model did not consult.
   *
   * Reads only. A write is not a source: it is a thing the answer did, and
   * listing it here would present a change as evidence.
   */
  sourcesOf(turn: ChatTurn): string[] {
    const seen: string[] = [];
    for (let i = 0; i < turn.tools.length; i++) {
      if (turn.tools[i].state === 'ok' && seen.indexOf(turn.tools[i].tool) === -1) {
        seen.push(turn.tools[i].tool);
      }
    }
    return seen;
  }

  shortName(name: string): string {
    return (name || '').replace(/^mcp__atlas__/, '');
  }

  copyAnswer(turn: ChatTurn): void {
    this.copy(turn.text);
    turn.copied = true;
    setTimeout(() => (turn.copied = false), 1500);
  }


  private copy(text: string): void {
    const clipboard = (navigator as any).clipboard;
    if (clipboard && clipboard.writeText) {
      clipboard.writeText(text);
    }
  }

  /** Re-ask the question that produced this turn, unchanged. */
  retry(turn: ChatTurn): void {
    const index = this.turns.indexOf(turn);
    const question = index > 0 ? this.turns[index - 1] : undefined;
    if (!question || question.role !== 'user' || this.busy) {
      return;
    }
    this.draft = question.text;
    this.send();
  }

  askFollowup(text: string): void {
    if (this.busy) {
      return;
    }
    this.draft = text;
    this.send();
  }

  toggleSources(turn: ChatTurn): void {
    turn.sourcesOpen = !turn.sourcesOpen;
  }

  /* ---- Turn plumbing ----------------------------------------------------- */

  private blankTurn(role: 'user' | 'assistant', text: string): ChatTurn {
    return {
      role: role,
      text: text,
      settledHtml: '',
      tail: [],
      reasoning: '',
      reasoningMs: 0,
      reasoningBase: 0,
      reasoningStart: 0,
      tools: [],
      tasks: [],
      docs: [],
      files: [],
      images: [],
      followups: [],
      ungrounded: [],
      notices: [],
      autoWrites: [],
      ungroundedOpen: false,
      notes: [],
      traceOpen: false,
      traceTouched: false,
      traceTab: 'steps',
      sourcesOpen: false,
      copied: false,
      live: false,
    };
  }

  /** The assistant turn currently being streamed into. */
  private liveTurn(): ChatTurn {
    let last = this.turns[this.turns.length - 1];
    if (!last || last.role !== 'assistant') {
      last = this.blankTurn('assistant', '');
      last.live = true;
      this.turns.push(last);
    }
    return last;
  }

  private appendAssistant(text: string): void {
    const last = this.liveTurn();
    // The first word of the answer ends the thinking phase.
    this.closeReasoningClock(last);
    last.text += text;
    this.split(last);
  }

  /**
   * Split the answer into settled Markdown and a streaming tail.
   *
   * The cut is the last blank line: everything before it is a finished block and
   * safe to render, everything after is still being written. The tail is words,
   * so `*ngFor` appends one span per word and each animates exactly once.
   */
  private split(turn: ChatTurn): void {
    const cut = turn.text.lastIndexOf('\n\n');
    if (cut === -1) {
      turn.settledHtml = '';
      turn.tail = turn.text.split(/(\s+)/).filter(part => part.length > 0);
      return;
    }
    turn.settledHtml = renderMarkdown(turn.text.substring(0, cut));
    turn.tail = turn.text
      .substring(cut + 2)
      .split(/(\s+)/)
      .filter(part => part.length > 0);
  }

  /** End of turn: the whole answer is settled, so it all becomes Markdown. */
  private settle(): void {
    this.busy = false;
    this.stopDictation();
    for (let i = 0; i < this.turns.length; i++) {
      const turn = this.turns[i];
      if (turn.role !== 'assistant' || !turn.live) {
        continue;
      }
      turn.live = false;
      // A fault with no answer above it IS the outcome, so it goes back into
      // the open. The action row that would otherwise carry it only renders
      // when the turn has text, so leaving it as a notice would show nothing
      // at all — an empty bubble where an error belongs.
      if (!turn.text.trim() && turn.notices.length) {
        turn.text = turn.notices.map(n => '[' + n + ']').join('\n\n');
        turn.notices = [];
      }
      turn.settledHtml = renderMarkdown(turn.text);
      turn.tail = [];
      // The composer's ghost now belongs to this answer, or to nothing.
      this.continueFrom(turn);
    }
  }

  private appendReasoning(text: string): void {
    const last = this.liveTurn();
    if (!last.reasoningStart) {
      last.reasoningStart = Date.now();
    }
    last.reasoning += text;
    // Settled bursts PLUS the one in flight.
    //
    // This used to be a bare `Date.now() - reasoningStart`, which is the length
    // of the current burst and nothing else. Since closeReasoningClock() zeroes
    // the start at every tool_start, the next burst restarted the clock and
    // overwrote the total — so a turn that thought 4s, ran three tools, and
    // thought 1s more before answering reported "1 saniye düşündü". The label
    // was always the last thought, never the turn.
    last.reasoningMs = last.reasoningBase + (Date.now() - last.reasoningStart);
    // The trace no longer opens itself while reasoning streams.
    //
    // It used to, on the grounds that reasoning was the only thing on screen
    // while it arrived. But it arrives on every turn, and what the operator
    // asked for is the answer: they were reading a wall of the model's working
    // that then collapsed under them the moment the reply began. The toggle
    // says what is happening — the label counts up and shimmers while the turn
    // is live — and one press opens it for anyone who wants to look.
  }

  private closeReasoningClock(turn: ChatTurn): void {
    if (turn.reasoningStart) {
      // Bank the burst and stop the clock. Whatever happens next — a tool call,
      // the answer — is not thinking and must not be counted as it.
      turn.reasoningBase += Date.now() - turn.reasoningStart;
      turn.reasoningMs = turn.reasoningBase;
      turn.reasoningStart = 0;
    }
    // There used to be a matching auto-collapse here, undoing the auto-open in
    // appendReasoning() once the thinking phase ended. With nothing opening the
    // trace on its own there is nothing left to put away.
  }

  /**
   * The SQL out of a tool's arguments, so it can be shown as code rather than
   * buried in JSON. Only `sql` — no other argument is a language.
   */

  private familyOf(name: string): string {
    const short = this.shortName(name);
    const cut = short.indexOf('_');
    return cut === -1 ? short : short.substring(0, cut);
  }

  private short(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }
    const json = JSON.stringify(value);
    return json.length > 120 ? json.substring(0, 120) + '…' : json;
  }

  private pretty(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return String(value);
    }
  }

  /**
   * Grow the composer to the text in it, up to the CSS ceiling.
   *
   * Reset to `auto` first: scrollHeight is measured against the current height,
   * so growing without the reset works and shrinking never does — the box only
   * ever ratchets upward.
   */
  private autoGrow(): void {
    if (!this.input || !this.input.nativeElement) {
      return;
    }
    this.reflow();
    const el = this.input.nativeElement as HTMLTextAreaElement;
    if (!this.draft) {
      // Empty: one line, always. scrollHeight would measure the PLACEHOLDER,
      // so a placeholder that wraps made the resting bar two lines tall — and
      // the empty bar is the one the operator looks at most.
      el.style.height = '';
      return;
    }
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }

  /**
   * Decide whether the draft still fits on the control row.
   *
   * Measured off a hidden copy of the text rather than off the textarea itself,
   * which is the only way this can be stable: asking "did the textarea wrap"
   * oscillates, because the moment it moves to its own full-width line the text
   * fits again, which would move it back. The hidden span has no width
   * constraint, so what it reports is the text's natural width and does not
   * depend on the layout the answer will produce.
   *
   * The threshold is the room the text would have on the control row, derived
   * from the row's own width minus the controls beside it — read from the DOM
   * rather than assumed, because the model chip's width depends on its name.
   */
  private reflow(): void {
    if (!this.composerRow || !this.measure) {
      return;
    }
    const row = this.composerRow.nativeElement;
    const span = this.measure.nativeElement;
    span.textContent = this.draft;

    let controls = 0;
    const kids = row.querySelectorAll(
      '.agent-plus, .agent-guard, .agent-model-btn, .agent-mic, .agent-send');
    for (let i = 0; i < kids.length; i++) {
      controls += (kids[i] as HTMLElement).offsetWidth + 4;
    }
    // 8px of slack so the caret is never flush against the model chip at the
    // moment the layout flips.
    const available = row.clientWidth - controls - 8;
    if (available <= 0) {
      return;
    }
    // A deliberate line break needs the full width even when every line in it
    // is short — `pre` reports the widest line, which for "a\nb" is tiny.
    this.wide =
      this.draft.indexOf('\n') >= 0 || span.offsetWidth > available;
  }

  private focusInput(): void {
    setTimeout(() => {
      if (this.input && this.input.nativeElement) {
        this.input.nativeElement.focus();
      }
    }, 0);
  }

  /**
   * Follow the transcript only while the operator is already at the bottom.
   *
   * This used to pin the view to the end on every stream event. Reasoning
   * arrives as a delta every few milliseconds, so any attempt to scroll up
   * during a turn was undone before the operator could read a line — the
   * scrollbar simply would not stay where it was put.
   *
   * Scrolling away now stops the following; scrolling back to the bottom
   * resumes it. `stick` is a fact about where the view IS, not a mode the
   * operator has to turn on and off.
   */
  private scrollSoon(): void {
    if (!this.stick) {
      return;
    }
    setTimeout(() => {
      // Re-checked inside the timeout as well: deltas arrive faster than this
      // fires, and a scroll that happened in between must win over a decision
      // taken before it.
      if (this.stick && this.scroller && this.scroller.nativeElement) {
        const el = this.scroller.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  }

  /**
   * Near enough to the bottom to count as "at the bottom".
   *
   * Not zero: a streaming line, a rounded scroll position and a growing element
   * all leave a few pixels, and an exact test would drop out of following on
   * its own halfway through a turn.
   */
  private static readonly STICK_SLACK = 56;

  onScroll(): void {
    if (!this.scroller || !this.scroller.nativeElement) {
      return;
    }
    const el = this.scroller.nativeElement;
    this.stick =
      el.scrollHeight - el.scrollTop - el.clientHeight <=
      AgentChatComponent.STICK_SLACK;
  }

  /** Back to the live end of the transcript, and follow it again from there. */
  scrollToBottom(): void {
    this.stick = true;
    if (this.scroller && this.scroller.nativeElement) {
      const el = this.scroller.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  /** ngFor identity for the streaming tail — index, so spans are only added. */
  trackByIndex(index: number): number {
    return index;
  }
}
